/**
 * Carnot efficiency, coefficients of performance and the second-law check for heat
 * engines. Temperatures are absolute (kelvin); energies in joules.
 *
 *   η_C = 1 − T_c / T_h                  best efficiency of any engine between two reservoirs
 *   COP_cool = T_c / (T_h − T_c)         refrigerator: heat removed per unit of work
 *   COP_heat = T_h / (T_h − T_c)         heat pump: heat delivered per unit of work
 *   W = Q_h − Q_c,  η = W / Q_h          first law for a cyclic engine
 *   ΔS = Q_c / T_c − Q_h / T_h ≥ 0       entropy generated; zero only for a reversible engine
 *
 * Reservoirs are assumed to hold constant temperatures, and the Carnot values are upper
 * limits: no real device reaches them.
 */

export type CarnotError =
  | "missing"
  | "absolute-zero"
  | "cold-above-hot"
  | "equal-temperatures"
  | "efficiency-range"
  | "non-positive"
  | "work-exceeds-heat"
  | "not-finite";

interface Failure {
  ok: false;
  error: CarnotError;
}

/** Relative tolerance for treating two floating-point efficiencies as equal. */
const REVERSIBLE_TOLERANCE = 1e-9;

/* ── Maximum efficiency ───────────────────────────────────────────────────── */

export type EngineTarget = "efficiency" | "hot" | "cold";

export interface EngineInputs {
  hot: number | null;
  cold: number | null;
  /** Fraction, 0 ≤ η < 1. */
  efficiency: number | null;
}

export interface EngineSolution {
  hot: number;
  cold: number;
  efficiency: number;
  /** Heat rejected per unit of heat absorbed, T_c / T_h. */
  rejectedShare: number;
}

const ENGINE_NEEDS: Record<EngineTarget, readonly (keyof EngineInputs)[]> = {
  efficiency: ["hot", "cold"],
  hot: ["cold", "efficiency"],
  cold: ["hot", "efficiency"],
};

function validEngineInputs(
  inputs: EngineInputs,
  target: EngineTarget
): CarnotError | null {
  for (const key of ENGINE_NEEDS[target]) {
    if (inputs[key] === null || !Number.isFinite(inputs[key])) {
      return "missing";
    }
  }
  const { hot, cold, efficiency } = inputs;
  if (
    (target !== "hot" && (hot ?? 0) <= 0) ||
    (target !== "cold" && (cold ?? 0) <= 0)
  ) {
    return "absolute-zero";
  }
  if (
    target !== "efficiency" &&
    !((efficiency ?? 0) >= 0 && (efficiency ?? 0) < 1)
  ) {
    return "efficiency-range";
  }
  if (target === "efficiency" && (cold ?? 0) > (hot ?? 0)) {
    return "cold-above-hot";
  }
  return null;
}

export function solveCarnotEngine(
  inputs: EngineInputs,
  target: EngineTarget
): { ok: true; value: EngineSolution } | Failure {
  const invalid = validEngineInputs(inputs, target);
  if (invalid) {
    return { ok: false, error: invalid };
  }
  let hot = inputs.hot ?? 0;
  let cold = inputs.cold ?? 0;
  let efficiency = inputs.efficiency ?? 0;
  if (target === "efficiency") {
    efficiency = 1 - cold / hot;
  } else if (target === "hot") {
    hot = cold / (1 - efficiency);
  } else {
    cold = hot * (1 - efficiency);
  }
  if (cold <= 0) {
    return { ok: false, error: "absolute-zero" };
  }
  const solution = { hot, cold, efficiency, rejectedShare: cold / hot };
  if (!Object.values(solution).every(Number.isFinite)) {
    return { ok: false, error: "not-finite" };
  }
  return { ok: true, value: solution };
}

/* ── Refrigerators and heat pumps ─────────────────────────────────────────── */

export interface CopSolution {
  hot: number;
  cold: number;
  /** Refrigerator coefficient of performance T_c / (T_h − T_c). */
  copCooling: number;
  /** Heat-pump coefficient of performance T_h / (T_h − T_c) = COP_cool + 1. */
  copHeating: number;
  /** With a work input W (J): heat removed from the cold side, J — else null. */
  heatRemoved: number | null;
  /** Heat delivered to the hot side, J — else null. */
  heatDelivered: number | null;
  work: number | null;
}

export function solveCop(
  hot: number | null,
  cold: number | null,
  work: number | null
): { ok: true; value: CopSolution } | Failure {
  if (hot === null || cold === null) {
    return { ok: false, error: "missing" };
  }
  if (!(hot > 0 && cold > 0)) {
    return { ok: false, error: "absolute-zero" };
  }
  if (cold > hot) {
    return { ok: false, error: "cold-above-hot" };
  }
  if (cold === hot) {
    return { ok: false, error: "equal-temperatures" };
  }
  if (work !== null && !(work > 0)) {
    return { ok: false, error: "non-positive" };
  }
  const gap = hot - cold;
  const copCooling = cold / gap;
  const copHeating = hot / gap;
  const solution: CopSolution = {
    hot,
    cold,
    copCooling,
    copHeating,
    heatRemoved: work === null ? null : copCooling * work,
    heatDelivered: work === null ? null : copHeating * work,
    work,
  };
  if (!Number.isFinite(copHeating)) {
    return { ok: false, error: "not-finite" };
  }
  return { ok: true, value: solution };
}

/* ── Heat flows of a real engine ──────────────────────────────────────────── */

export type FlowKnown = "rejected" | "work";

export interface FlowSolution {
  hot: number;
  cold: number;
  /** Heat absorbed from the hot reservoir, J. */
  heatIn: number;
  /** Heat rejected to the cold reservoir, J. */
  heatOut: number;
  work: number;
  efficiency: number;
  carnotEfficiency: number;
  /** Most work any engine could draw from the same heat, η_C Q_h, J. */
  maxWork: number;
  /** η / η_C. */
  secondLawEfficiency: number;
  /** Entropy generated per cycle, J/K. */
  entropyGenerated: number;
  /** Whether the engine beats the Carnot limit — impossible. */
  violatesSecondLaw: boolean;
  reversible: boolean;
}

export function solveHeatFlows(
  hot: number | null,
  cold: number | null,
  heatIn: number | null,
  known: FlowKnown,
  other: number | null
): { ok: true; value: FlowSolution } | Failure {
  if (hot === null || cold === null || heatIn === null || other === null) {
    return { ok: false, error: "missing" };
  }
  if (!(hot > 0 && cold > 0)) {
    return { ok: false, error: "absolute-zero" };
  }
  if (cold > hot) {
    return { ok: false, error: "cold-above-hot" };
  }
  if (!(heatIn > 0) || other < 0) {
    return { ok: false, error: "non-positive" };
  }
  const work = known === "work" ? other : heatIn - other;
  const heatOut = known === "work" ? heatIn - other : other;
  if (work > heatIn || heatOut > heatIn || work < 0 || heatOut < 0) {
    return { ok: false, error: "work-exceeds-heat" };
  }
  const efficiency = work / heatIn;
  const carnot = 1 - cold / hot;
  const entropy = heatOut / cold - heatIn / hot;
  const solution: FlowSolution = {
    hot,
    cold,
    heatIn,
    heatOut,
    work,
    efficiency,
    carnotEfficiency: carnot,
    maxWork: carnot * heatIn,
    secondLawEfficiency: carnot === 0 ? Number.NaN : efficiency / carnot,
    entropyGenerated: entropy,
    violatesSecondLaw: efficiency > carnot * (1 + REVERSIBLE_TOLERANCE),
    reversible: Math.abs(efficiency - carnot) <= REVERSIBLE_TOLERANCE,
  };
  const numbers = [
    solution.efficiency,
    solution.carnotEfficiency,
    solution.maxWork,
    solution.entropyGenerated,
  ];
  if (!numbers.every(Number.isFinite)) {
    return { ok: false, error: "not-finite" };
  }
  return { ok: true, value: solution };
}
